/* eslint-disable no-nested-ternary */
import { Button } from '@components/atoms/Button';
import { Loader } from '@components/atoms/Loader';
import { SelectedAttributeValueInput } from '@sdk/gqlTypes/generatedTypes';
import { useUser } from '@sdk/hook/useUser';
import { clsx } from 'clsx';
import envelopeImage from 'images/envelope.svg';
import heartImage from 'images/heart_red.svg';
import shareImage from 'images/share.svg';
import arrowImage from 'images/subcategories.svg';
import { ReactElement, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { AddToWishlist } from './AddToWishlist';

import * as S from './styles';
import { ShareState } from './types';

interface Props {
  selectOption: (newState: ShareState) => void;
  selectedProductAttributes: SelectedAttributeValueInput[];
  productId: string;
  variantId: string;
  addedToWishlist: boolean;
}

export function SaveSelect({
  productId,
  selectOption,
  selectedProductAttributes,
  variantId,
  addedToWishlist,
}: Props): ReactElement {
  const intl = useIntl();
  const { user } = useUser();

  const addOrRemoveToWishlistHandler = useCallback(
    async (onClick): Promise<void> => {
      await onClick();
      if (addedToWishlist) {
        selectOption(ShareState.removeToWishlist);
      } else {
        selectOption(ShareState.addToWishlist);
      }
    },
    [selectOption]
  );

  return (
    <S.SaveSelect>
      <Button
        className="save-select__btn"
        font="base"
        onClick={() => selectOption(ShareState.save)}
      >
        <S.SaveSelectIcon>
          <img src={envelopeImage} alt="" />
        </S.SaveSelectIcon>
        <S.SaveSelectText>
          {intl.formatMessage({
            defaultMessage: 'Send to email',
            id: 'W2Y8Ay',
          })}
        </S.SaveSelectText>
        <S.SaveSelectIcon>
          <img className="arrow" src={arrowImage} alt="arrow-icon" />
        </S.SaveSelectIcon>
      </Button>
      <Button
        className="save-select__btn"
        font="base"
        onClick={() => selectOption(ShareState.share)}
      >
        <S.SaveSelectIcon>
          <img src={shareImage} alt="" />
        </S.SaveSelectIcon>
        <S.SaveSelectText>
          {intl.formatMessage({
            defaultMessage: 'Share to social networks',
            id: 'bhq3iV',
          })}
        </S.SaveSelectText>
        <S.SaveSelectIcon>
          <img className="arrow" src={arrowImage} alt="arrow-icon" />
        </S.SaveSelectIcon>
      </Button>
      {user && (
        <AddToWishlist
          productId={productId}
          variantId={variantId}
          selectedProductAttributes={selectedProductAttributes}
          addedToWishlist={addedToWishlist}
        >
          {({ onClick, loading }) => (
            <Button
              className={clsx('save-select__btn', {
                'save-select__btn--loading': loading,
              })}
              font="base"
              onClick={() => {
                addOrRemoveToWishlistHandler(onClick);
              }}
              disabled={loading}
            >
              <S.SaveSelectIcon>
                <img src={heartImage} alt="" />
              </S.SaveSelectIcon>
              <S.SaveSelectText>
                {addedToWishlist ? (
                  <span>
                    {intl.formatMessage({
                      defaultMessage: 'Remove from wishlist',
                      id: '6HOS3i',
                    })}
                  </span>
                ) : (
                  <span>
                    {intl.formatMessage({
                      defaultMessage: 'Save to wishlist',
                      id: 'Xo3t9z',
                    })}
                  </span>
                )}
              </S.SaveSelectText>
              <S.SaveSelectIcon>
                <img className="arrow" src={arrowImage} alt="arrow-icon" />
              </S.SaveSelectIcon>
              <S.SaveSelectLoader loading={loading}>
                <Loader />
              </S.SaveSelectLoader>
            </Button>
          )}
        </AddToWishlist>
      )}
    </S.SaveSelect>
  );
}
